<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import ExternalLink from 'lucide-vue-next/dist/esm/icons/external-link.js'; import Mic from 'lucide-vue-next/dist/esm/icons/mic.js'; import Square from 'lucide-vue-next/dist/esm/icons/square.js';
import {
  NxBanner,
  NxChatMessage,
  NxChatThread,
  NxEmptyState,
  NxFloatingPanel,
  NxIconButton,
  NxSpinner,
  NxStatusDot,
} from '@blpde/nx-ui';
import { useShellVoice } from '@/voice/useShellVoice';

const { t } = useI18n();
const router = useRouter();
const {
  open,
  phase,
  error,
  agent,
  conversation,
  contextLine,
  turns,
  isRecording,
  isBusy,
  closeVoice,
  startRecording,
  stopRecordingAndSend,
} = useShellVoice();

/** Clear of the assistant button in the corner. */
const PANEL_MARGIN = { x: 20, y: 92 };
const PANEL_WIDTH = 'var(--nxt-panel-width-2xl)';
const PANEL_HEIGHT = 'min(calc(var(--nxt-size-unit) * 35), calc(100vh - var(--nxt-area-size-md)))';

const title = computed(() => t('connect.voice.title'));
const subtitle = computed(() => {
  if (contextLine.value) return contextLine.value;
  if (agent.value?.name) return agent.value.name;
  return t('connect.voice.subtitle');
});

const statusText = computed(() => {
  switch (phase.value) {
    case 'booting':
      return t('connect.voice.statusBooting');
    case 'recording':
      return t('connect.voice.statusRecording');
    case 'transcribing':
      return t('connect.voice.statusTranscribing');
    case 'thinking':
      return t('connect.voice.statusThinking');
    case 'error':
      return errorLabel.value;
    default:
      return t('connect.voice.statusReady');
  }
});

const errorLabel = computed(() => {
  switch (error.value) {
    case 'no-agent':
      return t('connect.voice.errNoAgent');
    case 'mic-denied':
      return t('connect.voice.errMic');
    case 'too-short':
      return t('connect.voice.errTooShort');
    case 'empty-transcript':
      return t('connect.voice.errEmpty');
    default:
      return error.value || t('connect.voice.errGeneric');
  }
});

const stickKey = computed(() => {
  const last = turns.value.at(-1);
  return `${turns.value.length}:${last?.content?.length ?? 0}:${last?.pending ? 1 : 0}`;
});

/** The panel's own close button and Escape end the session like the app's button does. */
function onOpenChange(isOpen: boolean) {
  if (!isOpen) closeVoice();
}

function onMicDown(e: PointerEvent) {
  e.preventDefault();
  (e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId);
  void startRecording();
}

function onMicUp(e: PointerEvent) {
  e.preventDefault();
  if (isRecording.value) void stopRecordingAndSend();
}

function openFullApp() {
  const id = conversation.value?.id;
  closeVoice();
  if (id) {
    void router.push({
      name: 'agent.Chat.Conversation',
      params: { conversationId: String(id) },
    });
    return;
  }
  void router.push('/agent');
}
</script>

<template>
  <NxFloatingPanel
    :open="open"
    :title="title"
    :subtitle="subtitle"
    position-key="connect.voice"
    :margin="PANEL_MARGIN"
    :width="PANEL_WIDTH"
    :height="PANEL_HEIGHT"
    :close-label="t('connect.voice.close')"
    :close-on-escape="false"
    flush
    @update:open="onOpenChange"
  >
    <template #header-leading>
      <span class="cx-voice__spark" aria-hidden="true">
        <Mic class="nxt-icon-md" />
      </span>
    </template>
    <template #header-actions>
      <NxIconButton
        height="xs"
        :title="t('connect.voice.openFull')"
        :aria-label="t('connect.voice.openFull')"
        @click="openFullApp"
      >
        <ExternalLink class="nxt-icon-md" />
      </NxIconButton>
    </template>

    <NxBanner v-if="phase === 'error'" variant="danger" class="cx-voice__banner">{{ statusText }}</NxBanner>
    <div v-else class="cx-voice__status">
      <NxSpinner v-if="isBusy && !isRecording" size="sm" />
      <NxStatusDot v-if="isRecording" variant="danger" />
      {{ statusText }}
    </div>

    <NxChatThread class="cx-voice__thread" :stick-key="stickKey" :near-bottom-px="48">
      <NxEmptyState v-if="turns.length === 0" compact :title="t('connect.voice.empty')" />
      <NxChatMessage
        v-for="m in turns"
        :key="m.id"
        :tone="m.role === 'user' ? 'user' : 'agent'"
        compact
      >
        <template #meta>
          <span>
            {{ m.role === 'user' ? t('connect.voice.you') : t('connect.voice.bot') }}
          </span>
          <span v-if="m.pending" class="cx-voice__pending">…</span>
        </template>
        <div class="cx-voice__bubble">
          <template v-if="m.content">{{ m.content }}</template>
          <template v-else-if="m.pending">{{ t('connect.voice.statusThinking') }}</template>
        </div>
        <NxBanner v-if="m.error" variant="danger">{{ m.error }}</NxBanner>
      </NxChatMessage>
    </NxChatThread>

    <div class="cx-voice__mic-bar">
      <p class="cx-voice__hint">
        {{ isRecording ? t('connect.voice.hintRelease') : t('connect.voice.hintHold') }}
      </p>
      <NxIconButton
        class="cx-voice__mic"
        height="xl"
        :variant="isRecording ? 'danger' : 'default'"
        :pressed="isRecording"
        :disabled="isBusy && !isRecording"
        :title="t('connect.voice.micLabel')"
        :aria-label="t('connect.voice.micLabel')"
        @pointerdown="onMicDown"
        @pointerup="onMicUp"
        @pointercancel="onMicUp"
        @pointerleave="isRecording && onMicUp($event)"
      >
        <Square v-if="isRecording" :fill="'currentColor'" class="nxt-icon-lg" />
        <Mic v-else class="nxt-icon-xl" />
      </NxIconButton>
    </div>
  </NxFloatingPanel>
</template>

<style scoped>
.cx-voice__spark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  flex-shrink: 0;
}

.cx-voice__status {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) calc(var(--nxt-space-md) * 0.875);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  flex-shrink: 0;
}

.cx-voice__thread {
  flex: 1;
  min-height: 0;
  padding: calc(var(--nxt-space-xs) * 1.25) var(--nxt-space-sm);
}

.cx-voice__bubble {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  white-space: pre-wrap;
  word-break: break-word;
}

.cx-voice__pending {
  margin-left: var(--nxt-space-2xs);
  opacity: 0.7;
}

.cx-voice__mic-bar {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-sm) calc(var(--nxt-space-md) * 1.125);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-surface-sunken-bg);
}

.cx-voice__hint {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  text-align: center;
}

.cx-voice__mic {
  /* Push to talk: a press must never scroll or select. */
  touch-action: none;
  user-select: none;
}

.cx-voice__banner {
  flex-shrink: 0;
  margin: var(--nxt-space-xs) var(--nxt-space-sm) 0;
}
</style>
